import { Collapse, Offcanvas } from 'bootstrap';

function handleOffcanvasEvents(): void {
  const offcanvasOffsetElement = document.querySelector<HTMLElement>('header');
  const offcanvasButton = document.getElementById('headerOffcanvasButton');
  const offcanvas = document.getElementById('headerOffcanvas');

  if (offcanvasOffsetElement && offcanvasButton && offcanvas) {
    let offcanvasSyncInterval: ReturnType<typeof setInterval> | null = null;

    const syncOffcanvasTop = (): void => {
      const topOffset = offcanvasOffsetElement.getBoundingClientRect().bottom;
      offcanvas.style.top = `${topOffset}px`;
    };

    offcanvas.addEventListener('show.bs.offcanvas', () => {
      offcanvasButton.classList.add('offcanvas-open');
      syncOffcanvasTop();

      offcanvasSyncInterval = setInterval(syncOffcanvasTop, 150);
    });

    offcanvas.addEventListener('hide.bs.offcanvas', () => {
      offcanvasButton.classList.remove('offcanvas-open');
      if (offcanvasSyncInterval !== null) {
        clearInterval(offcanvasSyncInterval);
      }
      offcanvasSyncInterval = null;
    });
  }
}

function handleMainNavigationEvents(): void {
  const mainNavigationCollapses = document.querySelectorAll<HTMLElement>('.navigation-menu-dropdowns .collapse');
  const mainNavigationItemsContainer = document.querySelectorAll<HTMLElement>('.main-navigation-items');
  let collapseOpenCount = 0;

  if (mainNavigationCollapses.length && mainNavigationItemsContainer.length) {
    mainNavigationCollapses.forEach(collapse => {
      collapse.addEventListener('hide.bs.collapse', () => {
        collapseOpenCount--;
        if (collapseOpenCount <= 0) {
          mainNavigationItemsContainer.forEach(container => container.classList.remove('dropdown-open'));
        }
      });
      collapse.addEventListener('show.bs.collapse', () => {
        collapseOpenCount++;
        if (collapseOpenCount > 0) {
          mainNavigationItemsContainer.forEach(container => container.classList.add('dropdown-open'));
        }
      });
    });
  }
}

function handleSearchEvents(): void {
  const header = document.querySelector<HTMLElement>('.header');
  const searchButton = document.getElementById('headerSearchButton');
  const searchPanel = document.getElementById('headerSearchPanel');
  const searchInput = document.getElementById('headerSearchInput') as HTMLInputElement | null;
  const loginButton = document.getElementById('headerLoginButton');
  const loginPanel = document.getElementById('headerLoginPanel');
  const loginInput = document.getElementById('login-form-input') as HTMLInputElement | null;
  const loginOptions = document.getElementById('headerLoginOptions');
  const loginOptionsTrigger = document.getElementById('headerLoginOptionsTrigger');

  if (!(header && searchButton && searchPanel && loginButton && loginPanel)) {
    return;
  }

  const setSearchState = (isOpen: boolean): void => {
    header.classList.toggle('header-state-search', isOpen);
    searchButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      setLoginState(false);
      searchInput?.focus();
    }
  };

  const setLoginFlyoutState = (isOpen: boolean): void => {
    loginOptions?.classList.toggle('bank-login-alt-active', isOpen);
    loginOptionsTrigger?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  const setLoginState = (isOpen: boolean): void => {
    header.classList.toggle('header-state-login', isOpen);
    loginButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      setSearchState(false);
      loginInput?.focus();
    } else {
      setLoginFlyoutState(false);
    }
  };

  searchButton.addEventListener('click', (event) => {
    event.preventDefault();
    setSearchState(!header.classList.contains('header-state-search'));
  });

  loginButton.addEventListener('click', (event) => {
    event.preventDefault();
    setLoginState(!header.classList.contains('header-state-login'));
  });

  loginOptionsTrigger?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLoginFlyoutState(!loginOptions?.classList.contains('bank-login-alt-active'));
  });

  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    const clickedInsideSearch = searchButton.contains(target) || searchPanel.contains(target);
    const clickedInsideLogin = loginButton.contains(target) || loginPanel.contains(target);

    if (!clickedInsideSearch && header.classList.contains('header-state-search')) {
      setSearchState(false);
    }

    if (!clickedInsideLogin && header.classList.contains('header-state-login')) {
      setLoginState(false);
    }
  });
}

function handleKeyboardAndMouseEvents(): void {
  // On escape keypress, collapse all header collapses
  const headerCollapses = document.querySelectorAll<HTMLElement>('header .collapse, .main-navigation-wrapper .collapse');
  const headerContainer = document.querySelector<HTMLElement>('.header');
  const searchButton = document.getElementById('headerSearchButton');
  const loginButton = document.getElementById('headerLoginButton');
  const loginOptions = document.getElementById('headerLoginOptions');
  const loginOptionsTrigger = document.getElementById('headerLoginOptionsTrigger');
  if (headerCollapses.length) {
    document.addEventListener('keydown', (event) => {
      if (event?.key === 'Escape' || event?.key === 'Esc') {
        if (headerContainer?.classList.contains('header-state-search')) {
          headerContainer.classList.remove('header-state-search');
          searchButton?.setAttribute('aria-expanded', 'false');
        }

        if (headerContainer?.classList.contains('header-state-login')) {
          headerContainer.classList.remove('header-state-login');
          loginButton?.setAttribute('aria-expanded', 'false');
        }

        if (loginOptions?.classList.contains('bank-login-alt-active')) {
          loginOptions.classList.remove('bank-login-alt-active');
          loginOptionsTrigger?.setAttribute('aria-expanded', 'false');
        }

        headerCollapses.forEach((headerCollapse) => {
          const collapse = Collapse.getOrCreateInstance(headerCollapse, { toggle: false });
          collapse.hide();
        });
      }
    });
  }

  // On click outside main navigation, close all main navigation collapses
  const mainNavigationWrappers = Array.from(document.querySelectorAll('.main-navigation'));
  const mainNavigationCollapses = document.querySelectorAll<HTMLElement>('.main-navigation .collapse');
  if (mainNavigationWrappers.length && mainNavigationCollapses.length) {
    document.addEventListener('click', (event) => {
      const clickedMainNavigation = mainNavigationWrappers.some(wrapper => wrapper.contains(event.target as Node));
      if (!clickedMainNavigation) {
        mainNavigationCollapses.forEach((mainNavigationCollapse) => {
          const collapse = Collapse.getOrCreateInstance(mainNavigationCollapse, { toggle: false });
          collapse.hide();
        });
      }
    });
  }

  // On click outside header and main navigation, close header offcanvas
  const headerElement = document.querySelector<HTMLElement>('header');
  const headerOffcanvas = document.querySelector<HTMLElement>('#headerOffcanvas');
  if (headerElement && headerOffcanvas) {
    document.addEventListener('click', (event) => {
      if (!headerElement.contains(event.target as Node) && !mainNavigationWrappers.some(wrapper => wrapper.contains(event.target as Node))) {
        const offcanvasInstance = Offcanvas.getOrCreateInstance(headerOffcanvas, { toggle: false });
        offcanvasInstance.hide();
      }
    });
  }
}

function handleHeaderScrollState(): void {
  const header = document.querySelector<HTMLElement>('.header');
  if (!header) {
    return;
  }

  const scrollThresholdPx = 8;
  const sync = (): void => {
    const scrolled = window.scrollY > scrollThresholdPx;
    header.classList.toggle('header-state-scroll', scrolled);
  };

  sync();
  window.addEventListener('scroll', sync, { passive: true });
}

document.addEventListener('DOMContentLoaded', () => {
  handleOffcanvasEvents();
  handleSearchEvents();
  handleMainNavigationEvents();
  handleKeyboardAndMouseEvents();
  handleHeaderScrollState();
});
